import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { resolveImageUrl } from "@/lib/api";
import { formatPHP } from "@/lib/format";
import { ShoppingCart, Minus, Plus, CheckCircle2 } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [activeImage, setActiveImage] = useState("");
  const { add } = useCart();

  useEffect(() => {
    setProduct(null);
    api.get(`/products/${slug}`).then((r) => {
      setProduct(r.data);
      setActiveImage(r.data.featured_image);
    }).catch(() => setProduct(false));
  }, [slug]);

  if (product === null) {
    return <div className="min-h-[60vh] grid place-items-center text-msr-slate" data-testid="product-loading">Loading...</div>;
  }
  if (product === false) {
    return (
      <div className="min-h-[60vh] grid place-items-center" data-testid="product-not-found">
        <div className="text-center">
          <h1 className="font-heading font-extrabold text-3xl mb-3">Product not found</h1>
          <Link to="/shop" className="btn-msr-outline">Back to Shop</Link>
        </div>
      </div>
    );
  }

  const images = [product.featured_image, ...(product.gallery || [])].filter(Boolean);

  const handleAdd = () => {
    add(product, quantity);
    toast.success(`${product.name} × ${quantity} added to cart`);
  };

  return (
    <div data-testid={`product-detail-${product.slug}`}>
      <section className="bg-white border-b border-msr-line">
        <div className="msr-container py-10">
          <Link to="/shop" className="text-xs uppercase tracking-[0.18em] text-msr-blue hover:text-msr-blue-dark" data-testid="product-back-link">
            ← Back to Shop
          </Link>
          <div className="grid lg:grid-cols-12 gap-10 mt-6">
            <div className="lg:col-span-7">
              <div className="relative aspect-[4/3] bg-msr-mist border border-msr-line overflow-hidden">
                <img src={resolveImageUrl(activeImage)} alt={product.name} className="h-full w-full object-cover" data-testid="product-active-image" />
              </div>
              {images.length > 1 && (
                <div className="grid grid-cols-5 gap-2 mt-3">
                  {images.map((img, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setActiveImage(img)}
                      className={`aspect-square overflow-hidden border ${activeImage === img ? "border-msr-blue" : "border-msr-line"}`}
                      data-testid={`product-thumb-${i}`}
                    >
                      <img src={resolveImageUrl(img)} alt="" className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="lg:col-span-5 space-y-5">
              <div className="badge-soft-blue">{product.product_type}</div>
              <h1 className="font-heading font-extrabold text-3xl lg:text-4xl tracking-tight" data-testid="product-name">{product.name}</h1>
              <p className="text-msr-slate leading-relaxed" data-testid="product-short-description">{product.short_description}</p>
              <div className="flex items-baseline gap-3 pt-2">
                <span className="font-heading font-extrabold text-4xl text-msr-blue" data-testid="product-price">{formatPHP(product.price)}</span>
                {product.compare_at_price > product.price && (
                  <span className="text-base text-msr-slate line-through">{formatPHP(product.compare_at_price)}</span>
                )}
              </div>
              <div className="text-xs uppercase tracking-wider text-msr-slate">SKU: {product.sku} · Stock: {product.stock_quantity}</div>

              <div className="flex items-center gap-3 pt-4">
                <div className="flex items-center border border-msr-line">
                  <button onClick={() => setQuantity((q) => Math.max(1, q - 1))} className="p-3 text-msr-ink hover:bg-msr-mist" data-testid="product-qty-decrease">
                    <Minus size={14} />
                  </button>
                  <span className="px-4 font-semibold" data-testid="product-qty">{quantity}</span>
                  <button onClick={() => setQuantity((q) => q + 1)} className="p-3 text-msr-ink hover:bg-msr-mist" data-testid="product-qty-increase">
                    <Plus size={14} />
                  </button>
                </div>
                <button onClick={handleAdd} className="btn-msr-primary flex-1" data-testid="product-add-to-cart-btn">
                  <ShoppingCart size={14} /> Add to Cart
                </button>
              </div>

              {(product.specifications || []).length > 0 && (
                <div className="pt-6 border-t border-msr-line">
                  <div className="overline mb-3">Specifications</div>
                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                    {product.specifications.map((s, i) => (
                      <div key={i} className="flex items-start justify-between gap-3 py-1.5 border-b border-msr-line/60" data-testid={`product-spec-${i}`}>
                        <dt className="text-msr-slate">{s.key}</dt>
                        <dd className="font-semibold text-msr-ink text-right">{s.value}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {product.description && (
        <section className="bg-msr-surface border-b border-msr-line">
          <div className="msr-container py-16 grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-7">
              <div className="overline mb-3">Description</div>
              <h2 className="font-heading font-extrabold text-2xl lg:text-3xl tracking-tight mb-5">About this product</h2>
              <p className="text-msr-slate leading-relaxed whitespace-pre-line" data-testid="product-description">{product.description}</p>
            </div>
            <div className="lg:col-span-5">
              <div className="msr-card border-t-4 border-t-msr-green">
                <div className="overline mb-3">What's included</div>
                <ul className="space-y-2.5 text-sm">
                  <Included text="Manufacturer warranty" />
                  <Included text="Free delivery in Metro Manila" />
                  <Included text="Optional installation services" />
                  <Included text="MSR after-sales support" />
                </ul>
              </div>
            </div>
          </div>
        </section>
      )}

      {(product.related || []).length > 0 && (
        <section className="bg-white">
          <div className="msr-container py-16">
            <div className="overline mb-3">Related products</div>
            <h2 className="font-heading font-extrabold text-2xl lg:text-3xl tracking-tight mb-8">You might also need</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {product.related.map((r) => (
                <Link key={r.id} to={`/shop/${r.slug}`} className="msr-card group" data-testid={`product-related-${r.slug}`}>
                  <div className="relative -mx-6 -mt-6 lg:-mx-8 lg:-mt-8 mb-4 h-36 overflow-hidden bg-msr-mist">
                    <img src={resolveImageUrl(r.featured_image)} alt={r.name} className="h-full w-full object-cover" />
                  </div>
                  <h3 className="font-heading font-bold text-sm leading-snug">{r.name}</h3>
                  <div className="mt-2 font-heading font-extrabold text-msr-blue">{formatPHP(r.price)}</div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

function Included({ text }) {
  return (
    <li className="flex items-start gap-2 text-msr-ink">
      <CheckCircle2 size={16} className="text-msr-green flex-shrink-0 mt-0.5" />
      <span>{text}</span>
    </li>
  );
}
